import { useEffect, useEffectEvent } from 'react';
import type { JourneyStepDirection } from './useWaypointFocus';

const ESCAPE_KEY = 'escape';
const STEP_BACK_KEY = 'arrowleft';
const STEP_FORWARD_KEY = 'arrowright';
const TYPING_TARGET_SELECTOR = 'input, textarea, select, [role=dialog]';
const MAP_CONTAINER_SELECTOR = '.leaflet-container';

interface UseMoTravelShortcutsParams {
  canStepJourney: boolean;
  onEscape: () => void;
  onJourneyStep: (direction: JourneyStepDirection) => void;
}

const hasModifierKey = (event: KeyboardEvent): boolean =>
  event.metaKey || event.ctrlKey || event.altKey;

export function useMoTravelShortcuts({
  canStepJourney,
  onEscape,
  onJourneyStep,
}: UseMoTravelShortcutsParams) {
  const handleWindowKeyDown = useEffectEvent((event: KeyboardEvent) => {
    const target = event.target instanceof Element ? event.target : null;
    if (hasModifierKey(event) || target?.closest(TYPING_TARGET_SELECTOR)) return;

    const pressedKey = event.key.toLowerCase();
    if (pressedKey === ESCAPE_KEY) {
      onEscape();
      return;
    }

    if (pressedKey === STEP_BACK_KEY || pressedKey === STEP_FORWARD_KEY) {
      if (!canStepJourney || target?.closest(MAP_CONTAINER_SELECTOR)) return;
      event.preventDefault();
      onJourneyStep(pressedKey === STEP_FORWARD_KEY ? 1 : -1);
    }
  });

  useEffect(() => {
    const listener = (event: KeyboardEvent) => handleWindowKeyDown(event);
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, []);
}
